<template>
  <div data-component-name="ArticleMarkup">
    <div
      v-html="markup"
      v-photo-swipe="{
        caption: isTouchDevice
          ? $t('ArticleMarkup.zoom-caption-pinch')
          : $t('ArticleMarkup.zoom-caption-scroll', { icon: SvgZoomIn }),
      }"
      class="markup"
    />
  </div>
</template>

<script lang="ts" setup>
// directives
import vPhotoSwipe from '~/directives/photoswipe';

// icons
import SvgZoomIn from './assets/svg/zoom-in.svg?raw';

defineProps<{ markup?: string }>();

const isTouchDevice = computed(() => {
  if (import.meta.server) return false;
  return 'ontouchstart' in document;
});
</script>

<style lang="scss" scoped>
@use '_/mixins/typo';
@use '_/mixins/flex';
@use '_/mixins/size';
@use '_/mixins/media';
@use '_/mixins/component';
@use '_/fn';

[data-component-name='ArticleMarkup'] {
  &:deep(.markup) {
    p {
      @include typo.body-2;
      margin-bottom: 0.5rem;
    }

    sup {
      vertical-align: top;
      position: relative;
      top: -0.3rem;
    }

    h2 {
      @include typo.subtitle-1;
      margin-bottom: 1rem;

      @include media.tablet {
        padding-top: 0;
        margin: 1.5rem 0 1rem 0;
      }
    }

    h3 {
      @include typo.subtitle-2;
      margin-bottom: 1rem;

      @include media.tablet {
        padding-top: 0;
        margin: 1.5rem 0 1rem 0;
      }
    }

    a {
      @include component.app-link;
      @include typo.link-text-2;
      word-break: break-word;
    }

    blockquote {
      position: relative;
      margin: 1rem 0 0.5rem 0;
      padding: 2rem;
      background-color: fn.token('surf-cont-secondary');

      @include flex.start-start;
      gap: 0 0.5rem;

      @include media.tablet {
        padding: 1.5rem;
        margin-bottom: 0;
      }

      @include media.mobile {
        padding: 1rem;
      }

      .left {
        &.icon-bulb,
        &.icon-quote {
          position: relative;
          @include size.fixed(1.5rem);

          &:before {
            content: ' ';
            display: block;

            @include size.full;
          }
        }

        &.icon-bulb {
          background: url('data:image/svg+xml;base64,PHN2ZwogIHdpZHRoPSIyNCIKICBoZWlnaHQ9IjI0IgogIHZpZXdCb3g9IjAgMCAyNCAyNCIKICBmaWxsPSIjRjA2RjAyIgogIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIKPgogIDxwYXRoCiAgICBkPSJNOS45NzMwOCAxOEgxNC4wMjY5QzE0LjE1ODkgMTYuNzk4NCAxNC43NzIxIDE1LjgwNjUgMTUuNzY3NiAxNC43MjI2QzE1Ljg3OTcgMTQuNjAwNiAxNi41OTg4IDEzLjg1NjQgMTYuNjg0MSAxMy43NTAxQzE3LjUzMTggMTIuNjkzMSAxOCAxMS4zODUgMTggMTBDMTggNi42ODYyOSAxNS4zMTM3IDQgMTIgNEM4LjY4NjI5IDQgNiA2LjY4NjI5IDYgMTBDNiAxMS4zODQzIDYuNDY3NzQgMTIuNjkxNyA3LjMxNDYyIDEzLjc0ODRDNy40MDAwNCAxMy44NTUgOC4xMjA4MSAxNC42MDEyIDguMjMxNTQgMTQuNzIxOEM5LjIyNzY2IDE1LjgwNjQgOS44NDEwMyAxNi43OTg0IDkuOTczMDggMThaTTE0IDIwSDEwVjIxSDE0VjIwWk01Ljc1Mzk1IDE0Ljk5OTJDNC42NTY0NSAxMy42Mjk3IDQgMTEuODkxNSA0IDEwQzQgNS41ODE3MiA3LjU4MTcyIDIgMTIgMkMxNi40MTgzIDIgMjAgNS41ODE3MiAyMCAxMEMyMCAxMS44OTI1IDE5LjM0MjggMTMuNjMxNSAxOC4yNDQzIDE1LjAwMTRDMTcuNjI0IDE1Ljc3NDggMTYgMTcgMTYgMTguNVYyMUMxNiAyMi4xMDQ2IDE1LjEwNDYgMjMgMTQgMjNIMTBDOC44OTU0MyAyMyA4IDIyLjEwNDYgOCAyMVYxOC41QzggMTcgNi4zNzQ1OCAxNS43NzM2IDUuNzUzOTUgMTQuOTk5MlpNMTMgMTAuMDA0OEgxNS41TDExIDE2LjAwNDhWMTIuMDA0OEg4LjVMMTMgNlYxMC4wMDQ4WiIKICAgIGZpbGw9ImluaGVyaXQiCiAgLz4KPC9zdmc+Cg==');
        }

        &.icon-quote {
          background: url('data:image/svg+xml;base64,PHN2ZwogIHdpZHRoPSIyNCIKICBoZWlnaHQ9IjI0IgogIHZpZXdCb3g9IjAgMCAyNCAyNCIKICBmaWxsPSIjRjA2RjAyIgogIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIKPgogIDxwYXRoCiAgICBkPSJNOS4yNTExIDE5SDBMNS45MjA3IDVIMTJMOS4yNTExIDE5Wk0yMS4yNTExIDE5SDEyLjA1MjlMMTcuOTczNiA1SDI0TDIxLjI1MTEgMTlaIgogICAgZmlsbD0iaW5oZXJpdCIKICAvPgo8L3N2Zz4K');
        }
      }

      .right {
        p {
          &:last-of-type {
            margin-bottom: 0;
          }
        }
      }

      &:before {
        content: '';
        display: block;

        position: absolute;
        left: 0;
        top: 0;
        right: calc(100% - 0.1875rem);
        bottom: 0;

        background-color: fn.token('outline-action');
      }
    }

    > ul,
    > ol {
      margin-bottom: 0.5rem;
    }

    ul {
      li {
        position: relative;
        margin-left: 1.65rem;

        @include typo.body-2;

        &:before {
          content: '';
          display: block;

          @include size.fixed(0.4rem);
          background-color: fn.token('icon-primary');
          border-radius: 100px;

          position: absolute;
          top: 0.73rem;
          left: calc(0px - 1.01rem);
        }

        strong {
          @include typo.subtitle-2;
        }
      }
    }

    ol {
      list-style: decimal;

      li {
        margin-left: 1.6rem;
        @include typo.body-2;

        &::marker {
          @include typo.subtitle-2;
        }
      }
    }

    figure.table {
      margin-bottom: 1rem;

      table {
        width: 100%;
        border-collapse: collapse;

        thead {
          border-bottom: 2px solid fn.token('outline-primary');

          tr {
            th {
              padding: 0.5rem 0 0.4rem 0;
              text-align: left;
              @include typo.subtitle-3;
            }
          }
        }

        tbody {
          tr {
            border-bottom: 1px solid fn.token('outline-secondary');

            td {
              @include typo.body-3;
              padding: 0.5rem 0 0.45rem 0;
            }
          }
        }

        th,
        td {
          width: calc(100% / 5);

          @for $index from 2 through 5 {
            &:nth-child(#{$index}) {
              padding-left: calc(0.3rem * #{$index - 1});
            }
          }
        }
      }
    }

    img {
      display: block;
      margin: 0 auto;

      max-width: 100%;
      height: auto;
    }

    figure.image {
      margin: 1rem 0;

      figcaption {
        margin-top: 0.5rem;
        color: fn.token('text-secondary');
        @include typo.caption;
      }
    }

    .embed-container {
      iframe {
        @include size.full;
        aspect-ratio: 16 / 9;
      }
    }
  }
}
</style>
